/**
 * MAV_CMD 元数据：命令号、参数、中文标注与危险等级。
 *
 * ## 为什么是"查表"而不是"逐条复制"
 *
 * 上游 `MAV_CMD` 有近 200 条并且**一直在增删**（本项目快照里是 199 条）。
 * 如果把编号抄进这张表，上游每加一条、每改一个编号，本文件就静静地和飞控
 * 产生分歧 —— 而且往往以"命令下发到错误的编号"这种最难查的方式表现出来。
 *
 * 所以这里的做法是：
 * - **编号永远来自** `@hgc/mavlink` 的 `MAV_CMD` 枚举（即仓库里固定提交的
 *   `ardupilotmega.xml`）。本文件里没有任何一个手写的命令编号。
 * - **上游描述与参数说明**来自同一份定义的 notes 产物
 *   （`@hgc/mavlink/generated/notes.generated`，也就是枚举条目的 `<description>`
 *   与 `<param>` 标签）。
 * - 本文件只提供**上游没有的东西**：中文名、中文参数名、危险等级、
 *   机型限制。这些是"我们比上游多知道的事"，加上去不会与上游冲突。
 *
 * 结果是：把上游快照换成新版本、重跑一次 `npm run gen:mavlink`，
 * 命令号与英文说明自动跟上；中文标注保持不变（即使有中文标注的命令被上游删了，
 * 也只是这条标注变成一条查不到的死数据，不会导致解析出错）。
 *
 * ## 危险等级的用途
 *
 * {@link CommandRisk} 是界面二次确认的依据。默认值是 `'flight'`（保守），
 * 因为"漏确认一次"比"多确认一次"的代价不对称得多：前者可能让一条
 * 提前终止降落的命令一键发出，后者只是让用户多点一下。
 */

import {
  enumByName,
  enumEntry,
  enumEntryByValue,
  type CommandParamNote,
  type EnumEntryDefinition,
  type EnumNotes,
} from '@hgc/mavlink';
import { ENUM_NOTES } from '@hgc/mavlink/generated/notes.generated';
import type { VehicleKind } from './vehicles';

/**
 * 命令的用途分类。**由条目名字推导**（见 {@link deriveCategory}），
 * 不是逐条枚举 —— 上游新增的命令会自动落到合理的一类里。
 */
export type CommandCategory =
  | 'navigation'
  | 'condition'
  | 'action'
  | 'mission-control'
  | 'fence'
  | 'rally'
  | 'preflight'
  | 'telemetry'
  | 'vehicle'
  | 'payload'
  | 'system';

/** 分类的中文名，界面直接显示。 */
export const COMMAND_CATEGORY_LABELS: Readonly<Record<CommandCategory, string>> = {
  navigation: '航段',
  condition: '条件',
  action: '动作',
  'mission-control': '任务流程',
  fence: '围栏',
  rally: '备降点',
  preflight: '起飞前',
  telemetry: '遥测',
  vehicle: '载具控制',
  payload: '负载',
  system: '系统',
};

/**
 * 危险等级。数值越大越危险，界面可以按它排序或分级提示。
 *
 * - `'query'` —— 只读：请求一条消息或一个状态，不会改变飞控行为。
 * - `'configure'` —— 写入参数/配置：改的是下次启动或后续飞行的参数，
 *   一般不立刻改变当前飞行状态。
 * - `'flight'` —— 会立刻改变飞行状态或后续航段行为（航线、模式、速度、
 *   云台指向、围栏开关……）。
 * - `'critical'` —— 危险：可能立刻导致失控、掉高、停机、丢失航点。
 *   界面必须做二次确认，并且应该把后果写在确认框里。
 */
export type CommandRisk = 'query' | 'configure' | 'flight' | 'critical';

export const COMMAND_RISK_LABELS: Readonly<Record<CommandRisk, string>> = {
  query: '只读查询',
  configure: '写入参数',
  flight: '会改变飞行状态',
  critical: '危险：可能立即失控',
};

/** 排序权重，数值越大越危险。 */
export const COMMAND_RISK_RANK: Readonly<Record<CommandRisk, number>> = {
  query: 0,
  configure: 1,
  flight: 2,
  critical: 3,
};

/** 是否需要界面二次确认。 */
export function needsConfirmation(risk: CommandRisk): boolean {
  return risk === 'flight' || risk === 'critical';
}

/** 一条命令的参数元数据。参数序号是 MAVLink 的 1..7（`param1`…`param7`）。 */
export interface CommandParamMeta {
  readonly index: number;
  /** 中文名；上游没有 label 且本地也没标注时是 `参数 N`。 */
  readonly label: string;
  /** 上游标注的单位（原样保留英文，便于和上游文档对照）。 */
  readonly units: string | undefined;
  readonly min: number | undefined;
  readonly max: number | undefined;
  /** 该参数按哪个枚举解释（如 `MAV_BOOL`、`PRECISION_LAND_MODE`）。 */
  readonly enumName: string | undefined;
}

/** 一条命令的完整元数据。 */
export interface CommandMeta {
  readonly id: number;
  /** 上游枚举条目全名，如 `MAV_CMD_NAV_WAYPOINT`。 */
  readonly entryName: string;
  /** 中文名。未收录时为 `undefined`，界面应退回显示 `entryName`。 */
  readonly label: string | undefined;
  readonly category: CommandCategory;
  readonly risk: CommandRisk;
  /** 上游英文描述（原文，不做翻译），用于对照与兜底展示。 */
  readonly upstreamDescription: string | undefined;
  /** 上游 `MAV_CMD` 条目属性：可由 `COMMAND_LONG` / `COMMAND_INT` 直接下发。 */
  readonly dispatchable: boolean;
  /** 上游 `MAV_CMD` 条目属性：可以作为一个任务项放进航线。 */
  readonly missionItem: boolean;
  /** 该命令带经纬度参数。 */
  readonly hasLocation: boolean;
  /** 该命令是"到达点"，任务进度按它推进。 */
  readonly isDestination: boolean;
  /** 围栏定义命令。 */
  readonly fence: boolean;
  /** 备降点定义命令。 */
  readonly rally: boolean;
  readonly params: readonly CommandParamMeta[];
  /** 本地补充的中文说明（前提条件、坑、依据）。 */
  readonly note: string | undefined;
}

/* ───────────────────── 分类推导 ───────────────────── */

/** 这些命令在名字上看不出归属，单独列出来比在推导规则里堆前缀判断更清楚。 */
const MISSION_CONTROL_NAMES: ReadonlySet<string> = new Set([
  'MAV_CMD_MISSION_START',
  'MAV_CMD_DO_JUMP',
  'MAV_CMD_DO_JUMP_TAG',
  'MAV_CMD_JUMP_TAG',
  'MAV_CMD_DO_SET_MISSION_CURRENT',
  'MAV_CMD_DO_PAUSE_CONTINUE',
]);

const PREFLIGHT_NAMES: ReadonlySet<string> = new Set([
  'MAV_CMD_ACCELCAL_VEHICLE_POS',
  'MAV_CMD_RUN_PREARM_CHECKS',
  'MAV_CMD_FLASH_BOOTLOADER',
  'MAV_CMD_DO_START_MAG_CAL',
  'MAV_CMD_DO_ACCEPT_MAG_CAL',
  'MAV_CMD_DO_CANCEL_MAG_CAL',
  'MAV_CMD_FIXED_MAG_CAL',
  'MAV_CMD_FIXED_MAG_CAL_FIELD',
  'MAV_CMD_FIXED_MAG_CAL_YAW',
  'MAV_CMD_SET_FACTORY_TEST_MODE',
  'MAV_CMD_AIRFRAME_CONFIGURATION',
  'MAV_CMD_BATTERY_RESET',
  'MAV_CMD_SET_HAGL',
  'MAV_CMD_SET_EKF_SOURCE_SET',
  'MAV_CMD_START_RX_PAIR',
  'MAV_CMD_PREFLIGHT_UAVCAN',
  'MAV_CMD_DO_SEND_BANNER',
]);

const VEHICLE_NAMES: ReadonlySet<string> = new Set([
  'MAV_CMD_COMPONENT_ARM_DISARM',
  'MAV_CMD_DO_SET_MODE',
  'MAV_CMD_DO_SET_STANDARD_MODE',
  'MAV_CMD_DO_FLIGHTTERMINATION',
  'MAV_CMD_DO_MOTOR_TEST',
  'MAV_CMD_ACTUATOR_TEST',
  'MAV_CMD_CONFIGURE_ACTUATOR',
  'MAV_CMD_DO_ENGINE_CONTROL',
  'MAV_CMD_INJECT_FAILURE',
  'MAV_CMD_DEBUG_TRAP',
  'MAV_CMD_POWER_OFF_INITIATED',
  'MAV_CMD_ARM_AUTHORIZATION_REQUEST',
  'MAV_CMD_DO_SET_SAFETY_SWITCH_STATE',
  'MAV_CMD_DO_SET_ACTUATOR',
  'MAV_CMD_DO_SET_SERVO',
  'MAV_CMD_DO_REPEAT_SERVO',
  'MAV_CMD_DO_SET_RELAY',
  'MAV_CMD_DO_REPEAT_RELAY',
  'MAV_CMD_DO_SET_REVERSE',
  'MAV_CMD_CONTROL_HIGH_LATENCY',
  'MAV_CMD_DO_PARACHUTE',
]);

/** 名字里含这些片段的算负载（相机/云台/吊舱/投放）。 */
const PAYLOAD_NAME_FRAGMENTS: readonly string[] = [
  'CAMERA',
  'GIMBAL',
  'MOUNT',
  'DIGICAM',
  'VIDEO',
  'IMAGE',
  'PANORAMA',
  'STORAGE',
  'TRIGG',
  'SPRAYER',
  'WINCH',
  'GRIPPER',
  'PARACHUTE',
  'ILLUMINATOR',
  'PAYLOAD',
];

const QUERY_NAME_PREFIXES: readonly string[] = ['MAV_CMD_REQUEST_', 'MAV_CMD_GET_'];

/** 这些命令只改参数/配置，不立刻改变当前飞行状态。 */
const CONFIGURE_NAMES: ReadonlySet<string> = new Set([
  'MAV_CMD_DO_SET_PARAMETER',
  'MAV_CMD_PREFLIGHT_STORAGE',
  'MAV_CMD_PREFLIGHT_SET_SENSOR_OFFSETS',
  'MAV_CMD_SET_MESSAGE_INTERVAL',
  'MAV_CMD_SET_HAGL',
  'MAV_CMD_SET_EKF_SOURCE_SET',
  'MAV_CMD_AIRFRAME_CONFIGURATION',
  'MAV_CMD_LOGGING_START',
  'MAV_CMD_LOGGING_STOP',
  'MAV_CMD_SCRIPTING',
  'MAV_CMD_DO_SEND_SCRIPT_MESSAGE',
  'MAV_CMD_RUN_PREARM_CHECKS',
]);

/**
 * 危险命令的显式清单。这里**必须**逐条列 —— 危险与否无法从名字推导，
 * 而漏掉一条的后果是界面不弹确认框。
 *
 * 收入标准：一旦下发，可能在几秒内让飞机进入不可逆状态，
 * 或者让操作员失去对飞机的控制（终止飞行、停机、抛伞、解锁、改模式、
 * 烧写固件、注入故障……）。
 */
const CRITICAL_NAMES: ReadonlySet<string> = new Set([
  'MAV_CMD_DO_FLIGHTTERMINATION',
  'MAV_CMD_DO_PARACHUTE',
  'MAV_CMD_DO_MOTOR_TEST',
  'MAV_CMD_ACTUATOR_TEST',
  'MAV_CMD_CONFIGURE_ACTUATOR',
  'MAV_CMD_COMPONENT_ARM_DISARM',
  'MAV_CMD_DO_SET_MODE',
  'MAV_CMD_DO_SET_STANDARD_MODE',
  'MAV_CMD_DO_ENGINE_CONTROL',
  'MAV_CMD_DO_VTOL_TRANSITION',
  'MAV_CMD_DO_LAND_START',
  'MAV_CMD_DO_RALLY_LAND',
  'MAV_CMD_MISSION_START',
  'MAV_CMD_OVERRIDE_GOTO',
  'MAV_CMD_PREFLIGHT_REBOOT_SHUTDOWN',
  'MAV_CMD_PREFLIGHT_CALIBRATION',
  'MAV_CMD_PREFLIGHT_STORAGE',
  'MAV_CMD_ACCELCAL_VEHICLE_POS',
  'MAV_CMD_DO_START_MAG_CAL',
  'MAV_CMD_FLASH_BOOTLOADER',
  'MAV_CMD_INJECT_FAILURE',
  'MAV_CMD_DEBUG_TRAP',
  'MAV_CMD_SET_FACTORY_TEST_MODE',
  'MAV_CMD_POWER_OFF_INITIATED',
  'MAV_CMD_DO_SET_ACTUATOR',
  'MAV_CMD_ARM_AUTHORIZATION_REQUEST',
  'MAV_CMD_DO_SET_HOME',
]);

function deriveCategory(entryName: string): CommandCategory {
  if (entryName.includes('FENCE')) return 'fence';
  if (entryName.includes('RALLY')) return 'rally';
  if (entryName.startsWith('MAV_CMD_NAV_')) return 'navigation';
  if (entryName.startsWith('MAV_CMD_CONDITION_')) return 'condition';
  if (MISSION_CONTROL_NAMES.has(entryName)) return 'mission-control';
  if (entryName.startsWith('MAV_CMD_PREFLIGHT_') || PREFLIGHT_NAMES.has(entryName)) return 'preflight';
  if (entryName.startsWith('MAV_CMD_REQUEST_') || entryName.startsWith('MAV_CMD_GET_')) return 'telemetry';
  if (VEHICLE_NAMES.has(entryName)) return 'vehicle';
  for (const fragment of PAYLOAD_NAME_FRAGMENTS) {
    if (entryName.includes(fragment)) return 'payload';
  }
  if (entryName.startsWith('MAV_CMD_DO_')) return 'action';
  return 'system';
}

function deriveRisk(entryName: string): CommandRisk {
  if (CRITICAL_NAMES.has(entryName)) return 'critical';
  for (const prefix of QUERY_NAME_PREFIXES) {
    if (entryName.startsWith(prefix)) return 'query';
  }
  if (CONFIGURE_NAMES.has(entryName)) return 'configure';
  // 未知命令一律按"会改变飞行状态"处理：这是安全侧的保守默认，
  // 代价只是多弹一次确认框，而不是漏弹。
  return 'flight';
}

/* ───────────────────── 本地补充的中文标注 ───────────────────── */

interface CuratedCommand {
  /** 上游枚举条目全名。写错（拼错/上游删了）不会报错，只会让标注失效。 */
  readonly name: string;
  readonly label: string;
  /** 参数中文名，键是 1..7。上游已有的参数按序合并。 */
  readonly params?: Readonly<Record<number, string>>;
  readonly note?: string;
  /** 覆盖推导出来的危险等级（推导规则看不出的场合）。 */
  readonly risk?: CommandRisk;
  /** 覆盖推导出来的分类。 */
  readonly category?: CommandCategory;
}

/**
 * 常用命令的中文标注。
 *
 * 只收录界面真正常用的那些：航线编辑、任务控制、解锁、围栏/备降点、
 * 遥测订阅、起飞前校准。**没有收录的命令不会消失** ——
 * {@link allCommands} 只是"常用子集"，{@link commandByName} 对任何上游命令
 * 都会返回元数据（只是没有中文名）。
 *
 * 参数中文名只写有把握的：拿不准的参数名（例如上游的 "Xtrack Location"
 * 这种术语）留空，界面会退回显示上游英文 label —— 显示英文术语
 * 比显示一个猜错的翻译更不容易出事故。
 */
const CURATED_COMMANDS: readonly CuratedCommand[] = [
  /* ── 航段 ── */
  {
    name: 'MAV_CMD_NAV_WAYPOINT',
    label: '航点',
    params: {
      1: '停留时间',
      2: '到达半径',
      3: '通过半径',
      4: '偏航角',
      5: '纬度',
      6: '经度',
      7: '高度',
    },
    note: '固定翼忽略"停留时间"；"通过半径"为负表示反向绕行。',
  },
  {
    name: 'MAV_CMD_NAV_LOITER_UNLIM',
    label: '无限盘旋',
    params: { 3: '半径', 4: '偏航角', 5: '纬度', 6: '经度', 7: '高度' },
    note: '半径填 0 表示用飞控参数里的默认盘旋半径。此航点不会自动退出。',
  },
  {
    name: 'MAV_CMD_NAV_LOITER_TURNS',
    label: '盘旋指定圈数',
    params: {
      1: '圈数',
      2: '是否要求对准航向',
      3: '半径',
      4: '横向偏移侧',
      5: '纬度',
      6: '经度',
      7: '高度',
    },
  },
  {
    name: 'MAV_CMD_NAV_LOITER_TIME',
    label: '盘旋指定时间',
    params: {
      1: '盘旋时间',
      2: '是否要求对准航向',
      3: '半径',
      4: '横向偏移侧',
      5: '纬度',
      6: '经度',
      7: '高度',
    },
  },
  { name: 'MAV_CMD_NAV_RETURN_TO_LAUNCH', label: '返航', note: '从当前位置返回返航点并降落，不需要坐标参数。' },
  {
    name: 'MAV_CMD_NAV_LAND',
    label: '降落',
    params: { 1: '中止高度', 2: '降落模式', 3: '未使用', 4: '偏航角', 5: '纬度', 6: '经度', 7: '高度' },
    note: '经纬度留空（NaN/0）表示降在当前位置正下方。',
  },
  {
    name: 'MAV_CMD_NAV_TAKEOFF',
    label: '起飞',
    params: { 1: '俯仰角', 2: '未使用', 3: '标志位', 4: '偏航角', 5: '纬度', 6: '经度', 7: '高度' },
    risk: 'critical',
    note: '解锁状态下下发会立刻起飞。',
  },
  {
    name: 'MAV_CMD_NAV_CONTINUE_AND_CHANGE_ALT',
    label: '继续并以新高度飞行',
    params: { 1: '动作', 7: '高度' },
  },
  {
    name: 'MAV_CMD_NAV_LOITER_TO_ALT',
    label: '盘旋至指定高度',
    params: { 1: '是否要求对准航向', 2: '半径', 4: '横向偏移侧', 5: '纬度', 6: '经度', 7: '高度' },
  },
  {
    name: 'MAV_CMD_NAV_PATHPLANNING',
    label: '路径规划',
    params: { 1: '本地控制', 2: '全局控制', 4: '偏航角', 5: '纬度', 6: '经度', 7: '高度' },
  },
  {
    name: 'MAV_CMD_NAV_SPLINE_WAYPOINT',
    label: '样条航点',
    params: { 1: '停留时间', 5: '纬度', 6: '经度', 7: '高度' },
    note: '与前后航点之间走曲线；起点终点不要用样条，否则容易过冲。',
  },
  {
    name: 'MAV_CMD_NAV_ALTITUDE_WAIT',
    label: '爬升到高度并等待',
    params: { 1: '高度', 2: '下降速度', 3: '摆动时间' },
  },
  {
    name: 'MAV_CMD_NAV_VTOL_TAKEOFF',
    label: '垂直起降·起飞',
    params: { 2: '转换航向', 4: '偏航角', 5: '纬度', 6: '经度', 7: '高度' },
  },
  {
    name: 'MAV_CMD_NAV_VTOL_LAND',
    label: '垂直起降·降落',
    params: { 1: '降落选项', 3: '进近高度', 4: '偏航角', 5: '纬度', 6: '经度', 7: '地面高度' },
  },
  {
    name: 'MAV_CMD_NAV_GUIDED_ENABLE',
    label: '启用引导',
    params: { 1: '启用' },
    note: '固定翼专用：让飞机接受引导目标。',
  },
  {
    name: 'MAV_CMD_NAV_DELAY',
    label: '延时',
    params: { 1: '延时', 2: '小时', 3: '分钟', 4: '秒' },
  },
  {
    name: 'MAV_CMD_NAV_FENCE_RETURN_POINT',
    label: '围栏返航点',
    params: { 5: '纬度', 6: '经度', 7: '高度' },
    note: '写进围栏任务后飞控会用它作为围栏触发后的返航点。',
  },
  {
    name: 'MAV_CMD_NAV_FENCE_POLYGON_VERTEX_INCLUSION',
    label: '围栏·多边形顶点（包含）',
    params: { 1: '顶点总数', 2: '包含组', 5: '纬度', 6: '经度' },
    note: '顶点总数必须等于实际写入的顶点条数，飞控会据此校验围栏是否完整。',
  },
  {
    name: 'MAV_CMD_NAV_FENCE_POLYGON_VERTEX_EXCLUSION',
    label: '围栏·多边形顶点（排除）',
    params: { 1: '顶点总数', 5: '纬度', 6: '经度' },
  },
  {
    name: 'MAV_CMD_NAV_FENCE_CIRCLE_INCLUSION',
    label: '围栏·圆形（包含）',
    params: { 1: '半径', 2: '包含组', 5: '纬度', 6: '经度' },
  },
  {
    name: 'MAV_CMD_NAV_FENCE_CIRCLE_EXCLUSION',
    label: '围栏·圆形（排除）',
    params: { 1: '半径', 5: '纬度', 6: '经度' },
  },
  {
    name: 'MAV_CMD_NAV_RALLY_POINT',
    label: '备降点',
    params: { 5: '纬度', 6: '经度', 7: '高度' },
    note: '备降点任务的最后一个点之后应跟一条 NAV_LAND 或 NAV_RETURN_TO_LAUNCH。',
  },
  {
    name: 'MAV_CMD_NAV_SET_YAW_SPEED',
    label: '设定航向与速度',
    params: { 1: '航向', 2: '速度', 3: '是否为角度' },
  },
  {
    name: 'MAV_CMD_NAV_ARC_WAYPOINT',
    label: '圆弧航点',
    params: { 1: '圆弧角度', 5: '纬度', 6: '经度', 7: '高度' },
  },

  /* ── 条件 ── */
  { name: 'MAV_CMD_CONDITION_DELAY', label: '条件·延时', params: { 1: '延时' } },
  {
    name: 'MAV_CMD_CONDITION_CHANGE_ALT',
    label: '条件·改变高度',
    params: { 1: '变化率', 7: '高度' },
  },
  { name: 'MAV_CMD_CONDITION_DISTANCE', label: '条件·距离', params: { 1: '距离' } },
  {
    name: 'MAV_CMD_CONDITION_YAW',
    label: '条件·偏航',
    params: { 1: '角度', 2: '角速度', 3: '方向', 4: '是否相对角' },
  },

  /* ── 动作 ── */
  {
    name: 'MAV_CMD_DO_SET_MODE',
    label: '设置飞行模式',
    params: { 1: '模式标志', 2: '自定义模式', 3: '自定义子模式' },
    note: 'ArduPilot 只认"自定义模式"（对应本包的飞行模式表）；参数 1 需要带上 CUSTOM_MODE_ENABLED 位。',
    risk: 'critical',
  },
  {
    name: 'MAV_CMD_DO_JUMP',
    label: '跳转航点',
    params: { 1: '目标航点序号', 2: '重复次数' },
    note: '重复次数填 -1 表示无限循环；死循环的任务飞控不会替你打断。',
  },
  {
    name: 'MAV_CMD_DO_CHANGE_SPEED',
    label: '改变速度',
    params: { 1: '速度类型', 2: '速度', 3: '油门百分比' },
  },
  {
    name: 'MAV_CMD_DO_SET_HOME',
    label: '设置返航点',
    params: { 1: '使用当前位置', 2: '横滚', 3: '俯仰', 4: '偏航', 5: '纬度', 6: '经度', 7: '高度' },
    note: '参数 1 为 1 时用飞机当前位置作为返航点，此时忽略经纬度参数。',
    risk: 'critical',
  },
  {
    name: 'MAV_CMD_DO_SET_PARAMETER',
    label: '设置参数（按序号）',
    params: { 1: '参数序号', 2: '参数值' },
    note: '按**序号**改参数，写错序号会静默改到另一个参数上。日常改参数应当用 PARAM_SET 消息，而不是这条命令。',
    risk: 'configure',
  },
  { name: 'MAV_CMD_DO_SET_RELAY', label: '设置继电器', params: { 1: '实例', 2: '状态' }, risk: 'configure' },
  {
    name: 'MAV_CMD_DO_REPEAT_RELAY',
    label: '重复切换继电器',
    params: { 1: '实例', 2: '次数', 3: '时间' },
    risk: 'configure',
  },
  { name: 'MAV_CMD_DO_SET_SERVO', label: '设置舵机 PWM', params: { 1: '实例', 2: 'PWM' }, risk: 'configure' },
  {
    name: 'MAV_CMD_DO_REPEAT_SERVO',
    label: '重复舵机 PWM',
    params: { 1: '实例', 2: 'PWM', 3: '次数', 4: '时间' },
    risk: 'configure',
  },
  {
    name: 'MAV_CMD_DO_FLIGHTTERMINATION',
    label: '终止飞行',
    params: { 1: '终止（1 生效）' },
    note: '立即停桨/收油门，用于坠机前的最后手段，不可撤销。',
  },
  { name: 'MAV_CMD_DO_CHANGE_ALTITUDE', label: '改变高度', params: { 1: '高度', 2: '坐标系' } },
  {
    name: 'MAV_CMD_DO_SET_ACTUATOR',
    label: '直接设置执行器输出',
    params: { 1: '执行器 1 输出', 2: '执行器 2 输出', 3: '执行器 3 输出', 4: '执行器 4 输出', 5: '执行器 5 输出', 6: '执行器 6 输出', 7: '索引' },
    note: '绕过混控直接给执行器归一化输出，桨叶会真的动。',
  },
  {
    name: 'MAV_CMD_DO_LAND_START',
    label: '开始降落序列',
    params: { 5: '纬度', 6: '经度', 7: '高度' },
    note: '标记"从这里开始走降落流程"，RTL/AUTO 都会找它。',
  },
  { name: 'MAV_CMD_DO_RALLY_LAND', label: '前往备降点降落', params: { 1: '高度', 2: '速度' } },
  { name: 'MAV_CMD_DO_GO_AROUND', label: '复飞', params: { 1: '高度' } },
  {
    name: 'MAV_CMD_DO_REPOSITION',
    label: '重定位（引导）',
    params: { 1: '速度', 2: '位掩码', 3: '半径', 4: '偏航角', 5: '纬度', 6: '经度', 7: '高度' },
    note: '引导模式的"飞到某点"；也会被 AUTO 模式当作动态目标接受。',
  },
  { name: 'MAV_CMD_DO_PAUSE_CONTINUE', label: '暂停/继续任务', params: { 1: '继续' } },
  {
    name: 'MAV_CMD_DO_SET_ROI_LOCATION',
    label: '设置兴趣点（坐标）',
    params: { 1: '云台设备 ID', 5: '纬度', 6: '经度', 7: '高度' },
  },
  { name: 'MAV_CMD_DO_SET_ROI_NONE', label: '取消兴趣点', params: { 1: '云台设备 ID' } },
  {
    name: 'MAV_CMD_DO_DIGICAM_CONTROL',
    label: '相机拍照控制',
    params: { 1: '会话控制', 2: '绝对变焦', 3: '相对变焦', 4: '对焦', 5: '拍照指令' },
    note: '老式相机协议；ArduPilot 用参数 5 = 1 触发一次拍照。',
  },
  { name: 'MAV_CMD_DO_MOUNT_CONTROL', label: '云台控制', params: { 1: '俯仰', 2: '横滚', 3: '偏航', 7: '模式' } },
  {
    name: 'MAV_CMD_DO_SET_CAM_TRIGG_DIST',
    label: '按距离触发拍照',
    params: { 1: '距离', 2: '快门', 3: '是否触发', 4: '相机 ID' },
    note: '距离填 0 表示停止按距离触发。',
  },
  {
    name: 'MAV_CMD_DO_FENCE_ENABLE',
    label: '启用围栏',
    params: { 1: '启用', 2: '围栏类型' },
    note: '参数 1：0 关闭、1 开启、2 保持当前状态。关闭围栏会同时放行围栏动作。',
  },
  {
    name: 'MAV_CMD_DO_PARACHUTE',
    label: '降落伞',
    params: { 1: '动作' },
    note: '抛伞不可撤销；只在确认要弃机时使用。',
  },
  {
    name: 'MAV_CMD_DO_MOTOR_TEST',
    label: '电机测试',
    params: {
      1: '电机编号',
      2: '油门类型',
      3: '油门值',
      4: '超时',
      5: '电机个数',
      6: '测试顺序',
    },
    note: '会让电机真的转起来。务必拆桨或固定机身。',
  },
  { name: 'MAV_CMD_DO_GRIPPER', label: '抓取器', params: { 1: '抓取器 ID', 2: '动作' } },
  {
    name: 'MAV_CMD_DO_AUTOTUNE_ENABLE',
    label: '启用自动调参',
    params: { 1: '启用', 2: '轴' },
    note: '自动调参会在空中大幅摆动飞机，必须在足够高度、无风条件下执行。',
  },
  {
    name: 'MAV_CMD_DO_SEND_SCRIPT_MESSAGE',
    label: '发送脚本消息',
    params: { 1: '消息 ID', 2: '参数 1', 3: '参数 2', 4: '参数 3' },
  },
  { name: 'MAV_CMD_DO_AUX_FUNCTION', label: '触发辅助功能', params: { 2: '开关位置' } },
  {
    name: 'MAV_CMD_DO_ENGINE_CONTROL',
    label: '发动机控制',
    params: { 1: '启动发动机', 2: '冷启动', 3: '延迟高度', 4: '选项' },
  },
  {
    name: 'MAV_CMD_DO_SET_MISSION_CURRENT',
    label: '设置当前航点',
    params: { 1: '序号', 2: '是否重置任务' },
  },
  { name: 'MAV_CMD_DO_WINCH', label: '绞车', params: { 1: '实例', 2: '动作', 3: '长度', 4: '速度' } },
  {
    name: 'MAV_CMD_DO_VTOL_TRANSITION',
    label: '垂直起降·模式转换',
    params: { 1: '状态', 2: '是否立即' },
    note: '空中转换要留够高度，转换过程飞机会短暂失去部分升力。',
  },
  {
    name: 'MAV_CMD_DO_ORBIT',
    label: '环绕飞行',
    params: { 1: '半径', 2: '速度', 3: '偏航行为', 4: '圈数（弧度）', 5: '纬度/北向', 6: '经度/东向', 7: '高度' },
  },
  {
    name: 'MAV_CMD_DO_FOLLOW',
    label: '跟随目标',
    params: { 1: '系统 ID', 4: '高度模式', 5: '高度', 7: '降落时间' },
  },
  {
    name: 'MAV_CMD_DO_SET_SAFETY_SWITCH_STATE',
    label: '设置安全开关状态',
    params: { 1: '目标状态' },
  },
  { name: 'MAV_CMD_DO_SET_REVERSE', label: '倒车（车/船）', params: { 1: '倒车' } },
  { name: 'MAV_CMD_DO_INVERTED_FLIGHT', label: '倒飞（固定翼）', params: { 1: '倒飞' } },
  {
    name: 'MAV_CMD_DO_SET_GLOBAL_ORIGIN',
    label: '设置全局原点',
    params: { 5: '纬度', 6: '经度', 7: '高度' },
  },
  {
    name: 'MAV_CMD_DO_JUMP_TAG',
    label: '跳转到标签',
    params: { 1: '标签', 2: '重复次数' },
  },
  {
    name: 'MAV_CMD_DO_GUIDED_LIMITS',
    label: '引导限制',
    params: { 1: '超时', 2: '最低高度', 3: '最高高度', 4: '水平移动上限' },
  },

  /* ── 任务流程 ── */
  {
    name: 'MAV_CMD_MISSION_START',
    label: '开始任务',
    params: { 1: '起始航点', 2: '结束航点' },
    note: '解锁状态下下发会立刻开始执行航线。',
  },

  /* ── 起飞前 ── */
  {
    name: 'MAV_CMD_PREFLIGHT_CALIBRATION',
    label: '传感器校准',
    params: {
      1: '陀螺温度校准',
      2: '磁力计校准',
      3: '地面气压校准',
      4: '遥控校准',
      5: '加速度计校准',
      6: '罗盘或空速校准',
      7: '电调或气压校准',
    },
    note: '参数之间互斥，一次只能触发一项；校准过程中飞机不能移动。',
  },
  {
    name: 'MAV_CMD_PREFLIGHT_SET_SENSOR_OFFSETS',
    label: '设置传感器偏移',
    params: { 1: '传感器类型', 2: 'X 偏移', 3: 'Y 偏移', 4: 'Z 偏移' },
    risk: 'configure',
  },
  {
    name: 'MAV_CMD_PREFLIGHT_STORAGE',
    label: '参数存储操作',
    params: { 1: '参数动作', 3: '日志速率' },
    note: 'PARAM_RESET_ALL_DEFAULT 会清掉全部参数并重启，等于把飞机恢复出厂。',
  },
  {
    name: 'MAV_CMD_PREFLIGHT_REBOOT_SHUTDOWN',
    label: '重启/关机',
    params: { 1: '飞控', 2: '伴飞计算机', 3: '组件动作', 4: '组件 ID', 6: '条件' },
    note: '飞行中重启飞控等于失去控制。',
  },
  {
    name: 'MAV_CMD_ACCELCAL_VEHICLE_POS',
    label: '加速度计校准姿态',
    params: { 1: '姿态' },
    note: '由地面站按 6 个姿态依次下发，飞控逐个回传进度。',
  },
  {
    name: 'MAV_CMD_RUN_PREARM_CHECKS',
    label: '运行解锁前检查',
    risk: 'query',
  },
  { name: 'MAV_CMD_FLASH_BOOTLOADER', label: '烧写引导程序', params: { 5: '魔术数' }, risk: 'configure' },
  {
    name: 'MAV_CMD_DO_START_MAG_CAL',
    label: '开始磁力计校准',
    params: { 1: '磁力计掩码', 2: '失败重试', 3: '自动保存', 4: '延时', 5: '自动重启' },
  },
  { name: 'MAV_CMD_DO_ACCEPT_MAG_CAL', label: '接受磁力计校准', params: { 1: '磁力计掩码' } },
  { name: 'MAV_CMD_DO_CANCEL_MAG_CAL', label: '取消磁力计校准', params: { 1: '磁力计掩码' } },
  {
    name: 'MAV_CMD_FIXED_MAG_CAL_YAW',
    label: '已知航向的磁校准',
    params: { 1: '航向', 2: '罗盘掩码', 3: '纬度', 4: '经度' },
    note: '在地面已知航向时使用，比空中磁校准快得多。',
  },
  {
    name: 'MAV_CMD_AIRFRAME_CONFIGURATION',
    label: '机架配置',
    params: { 1: '起落架 ID', 2: '起落架位置' },
    risk: 'configure',
  },

  /* ── 载具控制 ── */
  {
    name: 'MAV_CMD_COMPONENT_ARM_DISARM',
    label: '解锁/上锁',
    params: { 1: '解锁', 2: '强制' },
    note: '参数 2 = 21196 是"强制解锁/上锁"，会跳过全部解锁前检查（空中强制上锁等于停桨）。',
  },

  /* ── 遥测 ── */
  { name: 'MAV_CMD_GET_HOME_POSITION', label: '获取返航点' },
  {
    name: 'MAV_CMD_SET_MESSAGE_INTERVAL',
    label: '设置消息发送间隔',
    params: { 1: '消息 ID', 2: '间隔（微秒，-1 恢复默认）', 7: '响应目标' },
    risk: 'configure',
    note: '间隔设得太大或太小会直接影响遥测带宽；界面上的"刷新率"就是它。',
  },
  {
    name: 'MAV_CMD_REQUEST_MESSAGE',
    label: '请求单条消息',
    params: { 1: '消息 ID', 2: '请求参数 1', 3: '请求参数 2', 4: '请求参数 3', 5: '请求参数 4', 6: '请求参数 5', 7: '响应目标' },
  },
  { name: 'MAV_CMD_REQUEST_AUTOPILOT_CAPABILITIES', label: '请求飞控能力', params: { 1: '版本' } },
  { name: 'MAV_CMD_REQUEST_PROTOCOL_VERSION', label: '请求协议版本', params: { 1: '协议版本' } },
  { name: 'MAV_CMD_REQUEST_FLIGHT_INFORMATION', label: '请求飞行信息', params: { 1: '飞行信息' } },

  /* ── 日志与脚本 ── */
  { name: 'MAV_CMD_LOGGING_START', label: '开始记录日志', params: { 1: '格式' }, risk: 'configure' },
  { name: 'MAV_CMD_LOGGING_STOP', label: '停止记录日志', risk: 'configure' },
  { name: 'MAV_CMD_SCRIPTING', label: '脚本控制', params: { 1: '脚本命令' }, risk: 'configure' },

  /* ── 负载 ── */
  {
    name: 'MAV_CMD_IMAGE_START_CAPTURE',
    label: '开始连续拍照',
    params: { 1: '相机 ID', 2: '拍照间隔', 3: '拍照张数', 4: '序号' },
  },
  { name: 'MAV_CMD_VIDEO_START_CAPTURE', label: '开始录像', params: { 1: '流 ID', 2: '状态频率', 3: '相机 ID' } },
  { name: 'MAV_CMD_VIDEO_STOP_CAPTURE', label: '停止录像', params: { 1: '流 ID', 2: '相机 ID' } },
];

/** 机型限制：只在"有直接依据"时才列入。 */
interface CommandVehicleRestriction {
  readonly supportedBy: readonly Exclude<VehicleKind, 'unknown'>[];
  readonly note: string;
}

const VTOL_RESTRICTION_NOTE =
  'VTOL 相关命令只对 QuadPlane（ArduPlane 的垂直起降机型）有意义：' +
  'ArduPlane 的 `GCS_MAVLink_Plane.cpp::handle_command_int_packet()` 里有 ' +
  '`MAV_CMD_DO_VTOL_TRANSITION` 的处理分支，其它固件没有 VTOL 子系统，' +
  '会回 `MAV_RESULT_UNSUPPORTED`。核对日期 2026-09-25，来源 ' +
  'https://raw.githubusercontent.com/ArduPilot/ardupilot/master/ArduPlane/GCS_MAVLink_Plane.cpp';

const COMMAND_VEHICLE_RESTRICTIONS: Readonly<Record<string, CommandVehicleRestriction>> = {
  MAV_CMD_DO_VTOL_TRANSITION: { supportedBy: ['plane'], note: VTOL_RESTRICTION_NOTE },
  MAV_CMD_NAV_VTOL_TAKEOFF: { supportedBy: ['plane'], note: VTOL_RESTRICTION_NOTE },
  MAV_CMD_NAV_VTOL_LAND: { supportedBy: ['plane'], note: VTOL_RESTRICTION_NOTE },
};

/**
 * 上游自己声明为"NOP 占位"的条目 —— 它们不是命令，只是枚举里的上界标记。
 *
 * 依据是上游 `MAV_CMD` 的 `<description>` 原文（"NOP - This command is only used
 * to mark the upper limit of the ... commands in the enumeration"）。
 * 这是唯一一类可以理直气壮地说"不支持"的条目：上游自己说它不是命令。
 */
const NON_COMMAND_ENTRIES: Readonly<Record<string, string>> = {
  MAV_CMD_NAV_LAST:
    '上游描述为 "NOP - This command is only used to mark the upper limit of the NAV/ACTION commands in the enumeration"',
  MAV_CMD_CONDITION_LAST:
    '上游描述为 "NOP - This command is only used to mark the upper limit of the CONDITION commands in the enumeration"',
  MAV_CMD_DO_LAST:
    '上游描述为 "NOP - This command is only used to mark the upper limit of the DO commands in the enumeration"',
};

/* ───────────────────── 查表实现 ───────────────────── */

/** 命令引用：数值编号或名字（`MAV_CMD_NAV_WAYPOINT` / `NAV_WAYPOINT`，大小写不敏感）。 */
export type CommandRef = number | string;

function resolveEntry(ref: CommandRef): EnumEntryDefinition | undefined {
  if (typeof ref === 'number') {
    if (!Number.isFinite(ref)) return undefined;
    return enumEntryByValue('MAV_CMD', ref);
  }
  const upper = ref.trim().toUpperCase();
  if (upper.length === 0) return undefined;
  if (/^\d+$/.test(upper)) return enumEntryByValue('MAV_CMD', Number(upper));
  // 先按全名查（上游条目就是这个名字）；再补 `MAV_CMD_` 前缀，
  // 兼容习惯上省略前缀的写法。
  const withPrefix = upper.startsWith('MAV_CMD_') ? upper : `MAV_CMD_${upper}`;
  return enumEntry('MAV_CMD', withPrefix);
}

const curatedByName = new Map<string, CuratedCommand>(
  CURATED_COMMANDS.map((curated) => [curated.name, curated]),
);

let commandNotesCache: EnumNotes | undefined;

function commandNotes(): EnumNotes | undefined {
  if (commandNotesCache === undefined) {
    commandNotesCache = ENUM_NOTES['MAV_CMD'];
  }
  return commandNotesCache;
}

function parseNumeric(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function buildParams(
  entryName: string,
  curated: CuratedCommand | undefined,
): readonly CommandParamMeta[] {
  const upstreamParams: readonly CommandParamNote[] = commandNotes()?.entries[entryName]?.params ?? [];
  const byIndex = new Map<number, CommandParamMeta>();

  for (const param of upstreamParams) {
    byIndex.set(param.index, {
      index: param.index,
      label: curated?.params?.[param.index] ?? param.label ?? `参数 ${param.index}`,
      units: param.units,
      min: parseNumeric(param.minValue),
      max: parseNumeric(param.maxValue),
      enumName: param.enumName,
    });
  }

  // 本地标注了但上游没写的参数也要补进来：上游漏写某个 <param> 是常事，
  // 而界面上少一个输入框比多一个更糟（用户没法填）。
  for (const [key, label] of Object.entries(curated?.params ?? {})) {
    const index = Number(key);
    if (byIndex.has(index)) continue;
    byIndex.set(index, {
      index,
      label,
      units: undefined,
      min: undefined,
      max: undefined,
      enumName: undefined,
    });
  }

  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

function buildCommandMeta(entry: EnumEntryDefinition): CommandMeta {
  const curated = curatedByName.get(entry.name);
  const upstreamDescription = commandNotes()?.entries[entry.name]?.description;
  return {
    id: entry.value,
    entryName: entry.name,
    label: curated?.label,
    category: curated?.category ?? deriveCategory(entry.name),
    risk: curated?.risk ?? deriveRisk(entry.name),
    upstreamDescription,
    dispatchable: entry.command === true,
    missionItem: entry.mission === true,
    hasLocation: entry.hasLocation === true,
    isDestination: entry.isDestination === true,
    fence: entry.fence === true,
    rally: entry.rally === true,
    params: buildParams(entry.name, curated),
    note: curated?.note,
  };
}

/** 按编号或名字取命令元数据。不存在的命令返回 `undefined`（不抛错）。 */
export function commandOf(ref: CommandRef): CommandMeta | undefined {
  const entry = resolveEntry(ref);
  return entry === undefined ? undefined : buildCommandMeta(entry);
}

/** 按编号取命令元数据。 */
export function commandById(id: number): CommandMeta | undefined {
  return commandOf(id);
}

/** 按名字取命令元数据。 */
export function commandByName(name: string): CommandMeta | undefined {
  return commandOf(name);
}

/**
 * 上游 `MAV_CMD` 中**全部**命令的元数据（含未被中文标注的那些）。
 *
 * 顺序与上游枚举一致（按编号升序），便于和上游定义逐行对照。
 */
export function allUpstreamCommands(): readonly CommandMeta[] {
  return (enumByName('MAV_CMD')?.entries ?? []).map((entry) => buildCommandMeta(entry));
}

/**
 * 有中文标注的常用命令。**不是**全部命令 —— 界面做"添加航点/动作"的
 * 选单时用这个；需要覆盖上游全部命令（例如日志里出现了一个没标注的命令号）
 * 时用 {@link allUpstreamCommands} 或 {@link commandOf}。
 */
export function allCommands(): readonly CommandMeta[] {
  return CURATED_COMMANDS.map((curated) => resolveEntry(curated.name))
    .filter((entry): entry is EnumEntryDefinition => entry !== undefined)
    .map((entry) => buildCommandMeta(entry))
    .sort((a, b) => a.id - b.id);
}

/** 命令编号；未收录返回 `undefined`。语义等价于 `commandOf(ref)?.id`。 */
export function commandId(ref: CommandRef): number | undefined {
  return resolveEntry(ref)?.value;
}

/**
 * 危险等级。**未收录的命令也会给出等级**（推导规则 + 保守默认 `'flight'`），
 * 只有连编号都不在上游表里时才返回 `undefined`。
 */
export function commandRisk(ref: CommandRef): CommandRisk | undefined {
  return commandOf(ref)?.risk;
}

/* ───────────────────── 机型支持 ───────────────────── */

/** 判定的依据类型。 */
export type CommandSupportBasis =
  /** 直接来自上游 `MAV_CMD` 条目的标记。 */
  | 'upstream'
  /** 本包按 ArduPilot 源码逐条核对过。 */
  | 'curated'
  /** 没有依据，按默认放行 —— 这个值本身就是"我们不知道"的诚实标记。 */
  | 'assumed';

export interface CommandSupport {
  readonly supported: boolean;
  readonly basis: CommandSupportBasis;
  readonly note: string;
}

/**
 * 这条命令在指定机型上是否受支持。
 *
 * 判定顺序：
 * 1. 命令不在上游 `MAV_CMD` 里 → 不支持（`basis: 'assumed'`）。
 * 2. 机型是 `'unknown'` → 不支持（无从判断）。
 * 3. 上游自己声明为 NOP 占位条目（{@link NON_COMMAND_ENTRIES}）→ 不支持
 *    （`basis: 'upstream'`，证据是上游的 `<description>`）。
 * 4. 命中 {@link COMMAND_VEHICLE_RESTRICTIONS}（目前只有 VTOL 三条）
 *    → 按核对结果回答（`basis: 'curated'`）。
 * 5. 其余 → 支持（`basis: 'assumed'`）。
 *
 * 第 5 步的 `'assumed'` 是刻意的：ArduPilot 里"某机型是否实现了某条命令"
 * 分散在 6 个固件的 `handle_command_*` 与 `AP_Mission` 的 switch 里，
 * 没有任何一处汇总。**没有逐机型核对就不编造否定结论** ——
 * 假阴性会让界面把可用按钮灰掉，比假阳性（让飞控自己回 DENIED）更难排查。
 * 界面应当把 `basis === 'assumed'` 表现为"未校验"，而不是"已确认支持"。
 *
 * ## 一个被推翻过的判据（留在这里以免后来者重犯）
 *
 * 曾经用"上游既没有 `command` 也没有 `mission` 标记 → 判定不支持"来识别
 * "面向相机/云台等组件、飞控不会执行"的命令。这个推断**被反例推翻**：
 * `MAV_CMD_DO_SEND_SCRIPT_MESSAGE`、`MAV_CMD_DO_SPRAYER` 在上游都没有这两个标记，
 * 但 ArduPilot 确实在 `AP_Mission` 里实现了它们。
 * 也就是说这两个属性更像"文档标记"而不是"支持性声明"，
 * 拿它做否定判据会给出错误的假阴性。现在缺失时只体现在 `note` 里作为提醒。
 */
export function isSupportedBy(kind: VehicleKind, ref: CommandRef): CommandSupport {
  const entry = resolveEntry(ref);
  if (entry === undefined) {
    return {
      supported: false,
      basis: 'assumed',
      note: `命令 ${String(ref)} 不在上游 MAV_CMD 表中`,
    };
  }
  if (kind === 'unknown') {
    return {
      supported: false,
      basis: 'assumed',
      note: `${entry.name}：载具类别未知，无法判断命令支持情况`,
    };
  }

  const nonCommand = NON_COMMAND_ENTRIES[entry.name];
  if (nonCommand !== undefined) {
    return { supported: false, basis: 'upstream', note: `${entry.name}：${nonCommand}` };
  }

  const restriction = COMMAND_VEHICLE_RESTRICTIONS[entry.name];
  if (restriction !== undefined) {
    const supported = restriction.supportedBy.includes(kind);
    return {
      supported,
      basis: 'curated',
      note: supported ? restriction.note : `${entry.name}：${restriction.note}`,
    };
  }

  const marked = entry.command === true || entry.mission === true;
  return {
    supported: true,
    basis: 'assumed',
    note: marked
      ? `${entry.name}：上游标记为可下发/可用于任务；本包未逐机型核对，按支持处理`
      : `${entry.name}：上游未标记为可下发命令或任务项（可能是面向相机/云台组件的命令）；` +
        '本包按支持处理，实际拒绝由飞控给出',
  };
}
